// Fichier : EditUser.tsx
// Ce fichier contient la définition et l'implémentation du composant React pour modifier un utilisateur existant.
// Il utilise React Hook Form et Zod pour la validation de formulaire, RTK Query Pour la gestion des mutations, et React Roastify pour Notifications .

import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useParams } from "react-router-dom";
import { useGetUserByIdQuery } from "@/state/usersApiSlice";
import formStyles from "./ViewUser.module.css";
import { userSchema } from "@/zod Schemas/zodUserSchema";
import { Checkbox } from "../ui/checkbox";

// Définition du type de données du formulaire basé sur le schéma Zod
type UserFormData = z.infer<typeof userSchema>;

// Fonction principale du composant EditUser
const ViewUser = () => {
  // Fonction de notification pour afficher un message de succès

  const navigate = useNavigate(); // Hook pour la navigation
  const { id } = useParams(); // Hook pour récupérer les paramètres de l'URL
  const { data: userData, isLoading, isError } = useGetUserByIdQuery(id!); // Requête pour obtenir les détails d'un utilisateur par ID , Pour remplir les champs.

  // Hook pour gérer le formulaire avec validation Zod
  const {
    register,
    reset,
    formState: { errors },
    control,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema), // zod Resolver pour la validation (userSchema défini dans le dossier Zod)
    defaultValues: {
      username: "",
      password: "",
      tenant_id: "",
      wordpress_domain: "",
      chatwoot_domain: "",
      wordpress_user: "",
      wordpress_password: "",
      chatwoot_user: "",
      chatwoot_password: "",
      wordpress_cookies: "",
      chatwoot_cookies: "",
      printing_capabilities: false,
      cashdrawer_capabilities: false,
      second_screen_capabilities: false,
      avoir_decaisser_capabilities: false,
    },
  });

  useEffect(() => {
    // useEffect pour remplir les champs du formulaire avec les données de l'utilisateur
    if (userData) {
      reset({
        username: userData.username,
        password: userData.password,
         tenant_id: userData.tenant_id.toString(),
        wordpress_domain: userData.wordpress_domain,
        chatwoot_domain: userData.chatwoot_domain,
        wordpress_user: userData.wordpress_user,
        wordpress_password: userData.wordpress_password,
        chatwoot_user: userData.chatwoot_user,
        chatwoot_password: userData.chatwoot_password,
        wordpress_cookies: userData.wordpress_cookies,
        chatwoot_cookies: userData.chatwoot_cookies,
        printing_capabilities: userData.printing_capabilities,
        cashdrawer_capabilities: userData.cashdrawer_capabilities,
        second_screen_capabilities: userData.second_screen_capabilities,
        avoir_decaisser_capabilities:
          userData.avoir_decaisser_capabilities,
      });
    }
  }, [userData, reset]); // une Fois la variable userData sera mise à jour , useEffect se déclenche et remplis les champs par les valeurs chargés.

  // Affichage conditionnel en fonction de l'état de la requête
  if (isLoading) return <p>Loading...</p>; // Afficher un message de chargement
  if (isError) return <p>Error loading user details</p>; // Afficher un message d'erreur

  return (
    <div className={formStyles.container}>
      <h3
        className="underline cursor-pointer mb-8 text-gray-500 text-lg"
        onClick={() => navigate("/users/all")}
      >
        Back to Users List
      </h3>
      <div className={formStyles["form-container"]}>
        <h2 className={formStyles["form-title"]}>User Informations</h2>
        <div className={formStyles["form-grid"]}>
          {/* Groupe de champs de formulaire pour le nom d'utilisateur */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>Username:</label>
            <input
              type="text"
              {...register("username")}
              className={formStyles["form-input"]}
              disabled
            />
            {errors.username && (
              <p className={formStyles["form-input-error"]}>
                {errors.username.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le mot de passe */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>Password:</label>
            <input
              type="password"
              {...register("password")}
              className={formStyles["form-input"]}
              disabled
            />
            {errors.password && (
              <p className={formStyles["form-input-error"]}>
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le rôle */}
          {/* <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>Role:</label>
            <select
              id="role"
              {...register("role")}
              name="role"
              required
              className={formStyles["form-input"]}
              disabled
            >
              <option value="USER">User</option>
              <option value="ADMIN">Admin</option>
            </select>
            {errors.role && (
              <p className={formStyles["form-input-error"]}>
                {errors.role.message}
              </p>
            )}
          </div> */}

          {/* Groupe de champs de formulaire pour le tenant ID */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>Tenant ID:</label>
            <input
              type="text"
              {...register("tenant_id")}
              className={formStyles["form-input"]}
              disabled
            />
            {errors.tenant_id && (
              <p className={formStyles["form-input-error"]}>
                {errors.tenant_id.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le domaine WordPress */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>
              WordPress Domain:
            </label>
            <input
              type="text"
              {...register("wordpress_domain")}
              className={formStyles["form-input"]}
              disabled
            />
            {errors.wordpress_domain && (
              <p className={formStyles["form-input-error"]}>
                {errors.wordpress_domain.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le domaine Chatwoot */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>Chatwoot Domain:</label>
            <input
              type="text"
              {...register("chatwoot_domain")}
              className={formStyles["form-input"]}
              disabled
            />
            {errors.chatwoot_domain && (
              <p className={formStyles["form-input-error"]}>
                {errors.chatwoot_domain.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour l'utilisateur WordPress */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>WordPress User:</label>
            <input
              type="text"
              {...register("wordpress_user")}
              className={formStyles["form-input"]}
              disabled
            />
            {errors.wordpress_user && (
              <p className={formStyles["form-input-error"]}>
                {errors.wordpress_user.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le mot de passe WordPress */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>
              WordPress Password:
            </label>
            <input
              type="password"
              {...register("wordpress_password")}
              className={formStyles["form-input"]}
              disabled
            />
            {errors.wordpress_password && (
              <p className={formStyles["form-input-error"]}>
                {errors.wordpress_password.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour l'utilisateur Chatwoot */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>Chatwoot User:</label>
            <input
              type="text"
              {...register("chatwoot_user")}
              className={formStyles["form-input"]}
              disabled
            />
          </div>

          {/* Groupe de champs de formulaire pour le mot de passe Chatwoot */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>
              Chatwoot Password:
            </label>
            <input
              type="password"
              {...register("chatwoot_password")}
              className={formStyles["form-input"]}
              disabled
            />
          </div>

          {/* Groupe de champs de formulaire pour le mot de passe Chatwoot */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>
              Wordpress Cookies:
            </label>
            <input
              type="text"
              {...register("wordpress_cookies")}
              className={formStyles["form-input"]}
              disabled
            />
            {errors.wordpress_cookies && (
              <p className={formStyles.errorMessage}>
                {errors.wordpress_cookies.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le mot de passe Chatwoot */}
          <div className={formStyles["form-group"]}>
            <label className={formStyles["form-label"]}>
              Chatwoot Cookies:
            </label>
            <input
              type="text"
              {...register("chatwoot_cookies")}
              className={formStyles["form-input"]}
              disabled
            />
            {errors.chatwoot_cookies && (
              <p className={formStyles.errorMessage}>
                {errors.chatwoot_cookies.message}
              </p>
            )}
          </div>

          {/* Groupe de cases à cocher pour les capacités */}
          <div className="flex gap-12">
            <div className="flex items-center">
              <label className={formStyles["form-checkbox-label"]}>
                Printing Capabilities:
              </label>

              <Controller
                name="printing_capabilities"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="h-6 w-6"
                    disabled
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <label className={formStyles["form-checkbox-label"]}>
                Cashdrawer Capabilities:
              </label>
              <Controller
                name="cashdrawer_capabilities"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="h-6 w-6"
                    disabled
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <label className={formStyles["form-checkbox-label"]}>
                Second Screen Capabilities:
              </label>

              <Controller
                name="second_screen_capabilities"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="h-6 w-6"
                    disabled
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <label className={formStyles["form-checkbox-label"]}>
                Delete Transactions Capabilities:
              </label>
              <Controller
                name="avoir_decaisser_capabilities"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="h-6 w-6"
                    disabled
                  />
                )}
              />
            </div>
          </div>

          {/* Bouton pour soumettre le formulaire */}
          <div className={formStyles["flex"]}>
            <button
              onClick={() => navigate(`/users/${id}/edit`)}
              type="button"
              className={formStyles["form-button"]}
            >
              Edit User
            </button>
            <button
              className={formStyles.backButton}
              onClick={() => navigate(`/users/${id}/tabs/edit`)}
            >
              View/Edit Tabs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewUser;
